import { createFileRoute, Link } from "@tanstack/react-router";
import { MarketingPage } from "@/components/MarketingPage";
import { buildSocialMeta, SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: buildSocialMeta({
      title: "Terms of Service — CoachQuick",
      description: "Read the terms of service that govern your use of CoachQuick.",
      path: "/terms",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/terms` }],
  }),
  component: TermsPage,
});


function TermsPage() {
  return (
    <MarketingPage>
      <article className="space-y-6">
        <header className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Terms of Service</h1>
          <p className="text-sm text-muted-foreground">Last updated: May 8, 2026</p>
        </header>

        <section className="space-y-3">
          <p>By creating an account or using CoachQuick, you agree to these terms. If you don't agree, please
            don't use the service.</p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold">Your account</h2>
          <p>You're responsible for keeping your login credentials secure and for all activity under your
            account. You must be at least 18 years old to register.</p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold">Acceptable use</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>Don't upload content that's unlawful, infringing, or harmful.</li>
            <li>Don't attempt to break, probe, or overload the service.</li>
            <li>Only enter client information you're authorized to store.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold">Beta and availability</h2>
          <p>CoachQuick is offered "as is" and "as available". During beta, features may change and we may
            occasionally have downtime. We do our best to back things up but recommend exporting data you
            depend on.</p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold">Billing</h2>
          <p>Paid plans (when introduced) renew automatically until canceled. You can cancel anytime from
            Billing in the app; cancellation takes effect at the end of your current period. Trials and
            promotional pricing are described in-app at signup.</p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold">Your content</h2>
          <p>You keep ownership of content you upload (notes, drills, photos, video). You grant us a limited
            license to host and process that content solely to operate the service for you.</p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold">Termination</h2>
          <p>You can stop using CoachQuick at any time. We may suspend or terminate accounts that violate these
            terms or pose a risk to the service.</p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold">Disclaimer & liability</h2>
          <p>To the fullest extent allowed by law, CoachQuick disclaims implied warranties and is not liable
            for indirect or consequential damages. Our total liability for any claim is limited to the amount
            you paid us in the 12 months before the claim.</p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold">Contact</h2>
          <p>Questions about these terms? Use our <Link to="/contact" className="underline">contact form</Link>.</p>
        </section>
      </article>
    </MarketingPage>
  );
}
